// #2141 (docs/score/announcer-ui-redesign.md §5) — the whole-show list's
// pure row-build/sort/filter logic. Its own view, not a band (AC1): it is
// the fifth thing the redesign doc describes, reached from its own NavRail
// item, never mounted alongside the four bands.
//
// Data source (AC8): ONLY the showId-scoped `GET /ride` call (score-api's
// listRides -- see useRidesForShow.js), no new API route, no per-class
// fan-out. score-api's ride.js `enrichRideNames` already stamps
// horseName/riderName/riderStatus/className onto every row in THAT SAME
// response, so those columns cost nothing extra. `classes` (already fetched
// by RingDataContext for every other band, zero extra cost) fills in
// ring/classNumber for a ride whose own denormalized fields are missing.
//
// `score` (the composite): score-api never denormalizes a ride's composite
// onto the ride record itself -- it is computed on demand, only inside the
// PER-CLASS `GET /class/:classId/results` route (apps/score-api/src/
// handlers/results.js line ~1049), which this view deliberately never
// calls (that would be exactly the per-class fan-out AC8 forbids). Card
// judge round 1 (2026-09-10) asked whether the batch ScoreSheets-for-rides
// read (`listScoreSheetsForRides`, #1961/#2070, already used show-wide by
// useRideSheets) could fill this in client-side -- it cannot, safely: the
// composite is gated behind `exposeScores` (results.js line ~938, #697,
// "withheld until every expected judge has confirmed... must not leak one
// judge's number while the others are still scoring"), and results.js's own
// header comment on `buildRideResult` (line ~502) says that gate is
// deliberately NOT reimplemented anywhere else, exactly to avoid two copies
// drifting and leaking a score early. So `score` reads `ride.composite`
// defensively (in case a future card denormalizes an already-exposed value)
// but is normally always absent here -- AC10 treats that as the expected
// common case, not a rare edge case. Findings posted to card #2141's comment
// thread; AC2's score clause is N/A for the same reason.

import { RIDE_STATUSES } from "score-common";
import { venueDayKey } from "./formatTime";
import { rideSectionText } from "./orderOfGo";

// Same abbreviation table as report-defs's results-summary-day.js
// divisionLabel() (card #2346): the rider's OWN competitor status
// (person.riderStatus / FVD tblRider.chrStatus), never ride.qualifying
// (a different signal, #2346's whole point). A boolean-ish leak
// ("Yes"/"No"/"True"/"False") is never a real division and must never
// render verbatim.
const DIVISION_LABELS = [
  [/^adult amateur$/i, "AA"],
  [/^open$/i, "O"],
  [/^jr\s*\/?\s*yr$/i, "JR/YR"],
];

export function divisionLabel(raw) {
  const v = typeof raw === "string" ? raw.trim() : "";
  if (!v || /^(no|yes|true|false)$/i.test(v)) return "";
  for (const [re, label] of DIVISION_LABELS) {
    if (re.test(v)) return label;
  }
  return v; // unrecognized code -- pass through rather than silently drop
}

// One flat row per ride, across every ring and every day (AC2).
//
// `statuses` (judge round 1 finding #1): a rideId -> status map from
// score-api's batch POST /rides/status (score-common's DERIVED_RIDE_STATUSES
// vocabulary -- PENDING/ENTERING_RING/IN_PROGRESS/CONFIRMED/COMPLETED/FINAL/
// SCRATCHED), the SAME server-derived source every other badge in this app
// reads (useRideStatuses, #544/#546). `ride.status` alone is only the raw
// FVD/admin-set field (RIDE_STATUSES -- a narrower vocabulary that never
// includes CONFIRMED/COMPLETED/FINAL); score-common/utils/ride-status.js's
// header comment is explicit that the badge vocabulary is "never stored on
// the ride... so no client recomputes it (card #544)". Without this overlay
// every scored ride renders PENDING regardless of how far it has actually
// progressed. `statuses` is optional (defaults to `{}`) so existing callers
// that only pass `{ rides, classes }` still get ride.status as a fallback,
// never a thrown error.
export function buildWholeShowRows({ rides = [], classes = [], statuses = {}, sectionStyle = null } = {}) {
  const classesById = new Map((classes || []).filter(Boolean).map((c) => [c.id, c]));
  return (rides || [])
    .filter(Boolean)
    .filter((ride) => !ride.resolvedIntoRideId) // same audit-link exclusion score-api's own list applies
    .map((ride) => {
      const cls = classesById.get(ride.classId);
      return {
        id: ride.id,
        number: ride.competitorNumber || ride.rideNumber || "",
        horse: ride.horseName || "",
        rider: ride.riderName || "",
        // #3296 -- "" when the rider is not drawn into a section.
        section: rideSectionText(ride.subsection, sectionStyle),
        classId: ride.classId || "",
        className: ride.className || cls?.className || cls?.name || "",
        classNumber: cls?.classNumber || cls?.number || "",
        ring: ride.ring || cls?.ring || "",
        dayKey: venueDayKey(ride.scheduledTime, ride.scheduledTimeZone),
        scheduledTime: ride.scheduledTime || "",
        scheduledTimeZone: ride.scheduledTimeZone,
        score: ride.composite ?? null,
        placing: ride.placing ?? null,
        status: (statuses || {})[ride.id] || ride.status || RIDE_STATUSES.PENDING,
        division: divisionLabel(ride.riderStatus),
      };
    });
}

export const SORT_COLUMNS = [
  "number",
  "horse",
  "rider",
  "className",
  "ring",
  "scheduledTime",
  "score",
  "placing",
  "status",
];

const NUMERIC_COLUMNS = new Set(["score", "placing"]);

function isEmptyValue(v) {
  return v === null || v === undefined || v === "";
}

function rawValue(row, column) {
  return row ? row[column] : undefined;
}

// Every column sorts, ascending and descending, on a header click (AC3).
// A row missing the sorted field (no score yet, an unplaced ride) sorts
// LAST regardless of direction -- flipping the arrow should group scored
// rides together either way, not interleave blanks depending on which end
// of the list they land on. Ties keep the incoming (time) order, so a
// re-sort on an already-sorted column is deterministic across renders.
export function sortWholeShowRows(rows, column, direction = "asc") {
  const dir = direction === "desc" ? -1 : 1;
  const indexed = (rows || []).map((row, index) => ({ row, index }));
  indexed.sort((a, b) => {
    const av = rawValue(a.row, column);
    const bv = rawValue(b.row, column);
    const aEmpty = isEmptyValue(av);
    const bEmpty = isEmptyValue(bv);
    if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
    if (aEmpty && bEmpty) return a.index - b.index;
    let cmp;
    if (NUMERIC_COLUMNS.has(column)) {
      cmp = Number(av) - Number(bv);
    } else if (column === "scheduledTime") {
      cmp = String(av) < String(bv) ? -1 : String(av) > String(bv) ? 1 : 0;
    } else {
      cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
    }
    if (cmp === 0) cmp = a.index - b.index;
    return cmp * dir;
  });
  return indexed.map((w) => w.row);
}

export const DEFAULT_FILTERS = {
  day: null,
  ring: null,
  classId: null,
  status: null,
  division: null,
  text: "",
};

// Filters compose (AC4): every active filter narrows the set further, and a
// zero-match combination is simply an empty result (AC10), never an error.
export function filterWholeShowRows(rows, filters = {}) {
  const f = { ...DEFAULT_FILTERS, ...filters };
  const text = (f.text || "").trim().toLowerCase();
  return (rows || []).filter((row) => {
    if (f.day && row.dayKey !== f.day) return false;
    if (f.ring && String(row.ring) !== String(f.ring)) return false;
    if (f.classId && row.classId !== f.classId) return false;
    if (f.status && row.status !== f.status) return false;
    if (f.division && row.division !== f.division) return false;
    if (text) {
      const haystack = `${row.horse} ${row.rider} ${row.number}`.toLowerCase();
      if (!haystack.includes(text)) return false;
    }
    return true;
  });
}

// Distinct, sorted option lists for the filter controls, derived from the
// FULL row set (not the currently-filtered one) so a filter never hides its
// own other options.
export function distinctFilterOptions(rows) {
  const days = new Set();
  const rings = new Set();
  const statuses = new Set();
  const divisions = new Set();
  const classesById = new Map();
  for (const row of rows || []) {
    if (row.dayKey) days.add(row.dayKey);
    if (row.ring) rings.add(row.ring);
    if (row.status) statuses.add(row.status);
    if (row.division) divisions.add(row.division);
    if (row.classId && !classesById.has(row.classId)) {
      classesById.set(row.classId, { classId: row.classId, className: row.className, classNumber: row.classNumber });
    }
  }
  return {
    days: [...days].sort(),
    rings: [...rings].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true })),
    statuses: [...statuses].sort(),
    divisions: [...divisions].sort(),
    classes: [...classesById.values()].sort((a, b) =>
      String(a.classNumber).localeCompare(String(b.classNumber), undefined, { numeric: true }),
    ),
  };
}
